import type {
  Markup,
  TcRegion,
  TcTcpsApiService,
} from '@data-access/connect/tc-tcps';
import type {
  ActiveItems,
  FeatureExtractionContextType,
  ProjectDataState,
} from '@mobile-mapping/providers';
import { resolveCreatedMarkups } from '@mobile-mapping/providers';
import type { ViewMode } from '@mobile-mapping/web3d';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import type { Measurement } from '@trimble/web3d-providers';
import { getMultipaneReservedData } from './get-multipane-reserved-data';
import type { SavedCadEditingState } from './multipane-view.types';

export type BuildNumarkReservedDataDeps = {
  readonly tcTcpsApi: Pick<TcTcpsApiService, 'getViewDetails'>;
  readonly region: TcRegion;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly layouts: PaneLayouts<ViewMode>;
  readonly activeItems: ActiveItems;
  readonly dataState: ProjectDataState;
  readonly featureExtractionContext?: FeatureExtractionContextType;
};

export type NumarkReservedSnapshot = {
  readonly markups: readonly Markup[];
  readonly measurements: readonly Measurement[];
  readonly pointCloudIds?: readonly string[];
};

export async function buildNumarkReservedData(
  deps: BuildNumarkReservedDataDeps,
  viewId: string,
  snapshot?: NumarkReservedSnapshot
): Promise<Record<string, unknown>> {
  const markups = snapshot?.markups ?? [];
  const measurements = snapshot?.measurements ?? [];

  const createdMarkups =
    markups.length > 0
      ? await resolveCreatedMarkups(
          deps.tcTcpsApi,
          deps.region,
          { id: viewId, markups: [...markups] },
          markups.length
        )
      : [];

  const cadEditingSnapshot = buildCadEditingSnapshot(
    deps.featureExtractionContext
  );

  return getMultipaneReservedData(
    deps.capabilityRegistry,
    deps.layouts,
    deps.activeItems,
    deps.dataState,
    createdMarkups,
    measurements,
    cadEditingSnapshot
  );
}

function buildCadEditingSnapshot(
  context: FeatureExtractionContextType | undefined
): SavedCadEditingState | undefined {
  // Before the layers load, visibleLayerIds is empty; saving it would hide
  // every layer when the view loads with FeatureExtraction on.
  // `initialized`, not `layersLoadStatus`: the status is `pending` during a
  // background refetch, while the applied layers are still valid.
  if (
    !context?.isFeatureExtractionEnabled() ||
    !context.editingState.initialized
  ) {
    return undefined;
  }
  const selectedLayerId = context.editingState.selectedLayerId;
  const desiredDrawingTool = context.desiredDrawingTool();
  return {
    ...(selectedLayerId !== undefined && { selectedLayerId }),
    ...(desiredDrawingTool !== undefined && { desiredDrawingTool }),
    // Saved even when empty: an absent field means a view that predates
    // visibility persistence, which restore must leave untouched.
    visibleLayerIds: Object.keys(context.editingState.visibleLayerIds),
  };
}
